// Code in a blog post's body: the editor's code block button, the language
// picker on each block, and code pasted in from other sites.
//
// A block is saved as Quill's own `<pre data-language="java">`. The website
// highlights it by that key and adds the label and a Copy button (vc_cms
// lib/codeBlocks). Nothing is highlighted in the editor: that would mean
// shipping a highlighter to every admin for the sake of a preview.

import { CODE_LANGUAGES, normalizeLanguage } from "./codeLanguages";

const CODE_BLOCK = "code-block";
const MODULE = "modules/codeBlocks";

// Quill draws the code block button with the inline code icon. A box around the
// same brackets tells the two apart.
const CODE_BLOCK_ICON =
  '<svg viewBox="0 0 18 18"><rect class="ql-stroke" x="2" y="3" width="14" height="12" rx="2" ry="2"/>'
  + '<polyline class="ql-even ql-stroke" points="7.5 6.5 5 9 7.5 11.5"/>'
  + '<polyline class="ql-even ql-stroke" points="10.5 6.5 13 9 10.5 11.5"/></svg>';

const lineLanguage = (line) => (line?.statics?.blotName === CODE_BLOCK ? line.formats()[CODE_BLOCK] : null);

/**
 * The language a new code block at `index` starts as: the block before it, else
 * the first in the post, else plain — a post about Java is Java all the way down.
 */
export function languageNear(quill, index) {
  let before = null;
  let first = null;
  for (const line of quill.getLines(0, quill.getLength())) {
    const lang = lineLanguage(line);
    if (!lang) continue;
    if (!first) first = lang;
    if (quill.getIndex(line) >= index) break;
    before = lang;
  }
  return normalizeLanguage(before || first) || "plain";
}

/**
 * The code block button: make the selected lines one block of code, or text
 * again. A block that ends the post gets a line below it — otherwise there is
 * nowhere to click to keep writing.
 */
export function toggleCodeBlock(quill, on) {
  const range = quill.getSelection(true);
  if (!on) {
    quill.formatLine(range.index, range.length, CODE_BLOCK, false, "user");
    return;
  }
  quill.formatLine(range.index, range.length, CODE_BLOCK, languageNear(quill, range.index), "user");
  const [last] = quill.getLine(range.index + range.length);
  if (last && quill.getIndex(last) + last.length() >= quill.getLength()) {
    const end = quill.getLength() - 1;
    quill.insertText(end, "\n", "user");
    quill.formatLine(end + 1, 1, CODE_BLOCK, false, "user");
  }
  quill.setSelection(range.index, range.length, "silent");
}

function classLanguage(el) {
  for (const cls of el?.classList || []) {
    const m = /^(?:language|lang)-(.+)$/i.exec(cls);
    const key = m && normalizeLanguage(m[1]);
    if (key) return key;
  }
  return null;
}

/**
 * The language of a pasted or loaded `<pre>`: our own `data-language`, else
 * the `language-*` class most sites put on it or on its `<code>`, else plain.
 * A language we do not offer becomes plain, which is what the website would
 * draw it as anyway.
 */
export function preLanguage(pre) {
  return normalizeLanguage(pre.getAttribute("data-language"))
    || classLanguage(pre)
    || classLanguage(pre.querySelector("code"))
    || "plain";
}

function attachPicker(quill, container) {
  if (container.statics.blotName !== "code-block-container" || container.uiNode != null) return;
  const doc = quill.root.ownerDocument;
  const select = doc.createElement("select");
  select.setAttribute("aria-label", "Code language");
  select.title = "Code language";
  CODE_LANGUAGES.forEach(({ key, label }) => {
    const option = doc.createElement("option");
    option.value = key;
    option.textContent = label;
    select.appendChild(option);
  });
  select.addEventListener("change", () => {
    quill.formatLine(quill.getIndex(container), container.length(), CODE_BLOCK, select.value, "user");
  });
  // Quill keeps the picker's value in step with the block from here (undo included).
  container.attachUI(select);
  select.value = lineLanguage(container.children.head) || "plain";
}

function labelToolbar(quill) {
  const toolbar = quill.getModule("toolbar")?.container;
  const block = toolbar?.querySelector("button.ql-code-block");
  if (block) {
    block.innerHTML = CODE_BLOCK_ICON;
    block.title = "Code block";
    block.setAttribute("aria-label", "Code block");
  }
  const inline = toolbar?.querySelector("button.ql-code");
  if (inline) {
    inline.title = "Inline code";
    inline.setAttribute("aria-label", "Inline code");
  }
}

/**
 * Register the `codeBlocks` module with this Quill. It is a module, not an
 * effect in the editor, so it follows every Quill instance ReactQuill makes —
 * the editor is rebuilt whenever its props change, and under StrictMode.
 */
export function registerCodeBlocks(Quill) {
  if (Quill.imports[MODULE]) return;
  const Module = Quill.import("core/module");
  const Delta = Quill.import("delta");

  class CodeBlocks extends Module {
    constructor(quill, options) {
      super(quill, options);
      quill.on(Quill.events.SCROLL_BLOT_MOUNT, (blot) => attachPicker(quill, blot));
      // Runs after Quill's own <pre> matcher, which reads only data-language.
      quill.clipboard.addMatcher("PRE", (node, delta) => {
        const lang = preLanguage(node);
        return new Delta(delta.ops.map((op) => (op.attributes?.[CODE_BLOCK]
          ? { ...op, attributes: { ...op.attributes, [CODE_BLOCK]: lang } }
          : op)));
      });
      labelToolbar(quill);
    }
  }
  Quill.register(MODULE, CodeBlocks);
}
